import axios from 'axios';

// Alpaca API credentials
const API_KEY_ID = process.env.VITE_ALPACA_KEY_ID;
const SECRET_KEY = process.env.VITE_ALPACA_SECRET_KEY;
let isPaperMode = process.env.VITE_ALPACA_PAPER_MODE === 'true';

// Data API URL (static)
const DATA_BASE_URL = 'https://data.alpaca.markets/v2';

// Axios clients
let tradeClient: any;
let dataClient: any;

// Initialize or re-configure clients
const configureClients = () => {
  // Determine trade API URL based on current mode
  const tradeBaseURL = isPaperMode
    ? 'https://paper-api.alpaca.markets'
    : 'https://api.alpaca.markets';
  tradeClient = axios.create({
    baseURL: tradeBaseURL,
    headers: {
      'APCA-API-KEY-ID': API_KEY_ID,
      'APCA-API-SECRET-KEY': SECRET_KEY,
    },
  });
  dataClient = axios.create({
    baseURL: DATA_BASE_URL,
    headers: {
      'APCA-API-KEY-ID': API_KEY_ID,
      'APCA-API-SECRET-KEY': SECRET_KEY,
    },
  });
};
configureClients();

/**
 * Toggle between paper and live mode
 */
export const setPaperMode = (paper: boolean): void => {
  isPaperMode = paper;
  configureClients();
};

// Asset cache to avoid repeatedly fetching all assets
const assetCache = {
  data: null as any[] | null,
  timestamp: 0,
  ttl: 24 * 60 * 60 * 1000, // 24 hours cache
  async getAssets() {
    if (this.data && Date.now() - this.timestamp < this.ttl) {
      return this.data;
    }
    try {
      const res = await tradeClient.get('/v2/assets', {
        params: { status: 'active', asset_class: 'us_equity' },
      });
      this.data = res.data;
      this.timestamp = Date.now();
      return this.data;
    } catch (error) {
      console.error('Failed to fetch assets:', error);
      return [];
    }
  }
};

/**
 * Alpaca service wrapper
 */
export const alpacaService = {
  getAccount: async (): Promise<any> => {
    const res = await axios.get('/api/alpaca/account');
    return res.data;
  },

  getLatestTrade: async (symbol: string): Promise<any> => {
    const res = await dataClient.get(`/stocks/${symbol}/trades/latest`);
    return res.data.trade;
  },

  getLatestQuote: async (symbol: string): Promise<any> => {
    const res = await dataClient.get(`/stocks/${symbol}/quotes/latest`);
    return res.data.quote;
  },

  getHistoricalBars: async (
    symbols: string[],
    timeframe: string,
    start: string,
    end: string,
    limit?: number
  ): Promise<Record<string, any[]>> => {
    const params: any = { symbols: symbols.join(','), timeframe, start, end };
    if (limit) params.limit = limit;
    const res = await dataClient.get('/stocks/bars', { params });
    return res.data.symbols || {};
  },

  validateSymbol: async (symbol: string): Promise<boolean> => {
    try {
      await tradeClient.get(`/v2/assets/${symbol}`);
      return true;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return false;
      }
      throw error;
    }
  },

  searchAssets: async (query: string): Promise<any[]> => {
    if (!query || query.trim().length === 0) {
      return [];
    }
    
    const queryUpper = query.toUpperCase();
    const queryLower = query.toLowerCase();
    
    // Enhanced static list of common symbols for fast search and fallback
    const commonSymbols = [
      { symbol: 'AAPL', name: 'Apple Inc.', exchange: 'NASDAQ', status: 'active', tradable: true },
      { symbol: 'MSFT', name: 'Microsoft Corporation', exchange: 'NASDAQ', status: 'active', tradable: true },
      { symbol: 'GOOGL', name: 'Alphabet Inc.', exchange: 'NASDAQ', status: 'active', tradable: true },
      { symbol: 'GOOG', name: 'Alphabet Inc.', exchange: 'NASDAQ', status: 'active', tradable: true },
      { symbol: 'AMZN', name: 'Amazon.com Inc.', exchange: 'NASDAQ', status: 'active', tradable: true },
      { symbol: 'TSLA', name: 'Tesla, Inc.', exchange: 'NASDAQ', status: 'active', tradable: true },
      { symbol: 'META', name: 'Meta Platforms Inc.', exchange: 'NASDAQ', status: 'active', tradable: true },
      { symbol: 'NVDA', name: 'NVIDIA Corporation', exchange: 'NASDAQ', status: 'active', tradable: true },
      { symbol: 'AMD', name: 'Advanced Micro Devices Inc.', exchange: 'NASDAQ', status: 'active', tradable: true },
      { symbol: 'NFLX', name: 'Netflix Inc.', exchange: 'NASDAQ', status: 'active', tradable: true },
      { symbol: 'SPY', name: 'SPDR S&P 500 ETF Trust', exchange: 'ARCA', status: 'active', tradable: true },
      { symbol: 'QQQ', name: 'Invesco QQQ Trust', exchange: 'NASDAQ', status: 'active', tradable: true },
      { symbol: 'ARKK', name: 'ARK Innovation ETF', exchange: 'ARCA', status: 'active', tradable: true },
      { symbol: 'VTI', name: 'Vanguard Total Stock Market ETF', exchange: 'ARCA', status: 'active', tradable: true },
      { symbol: 'IWM', name: 'iShares Russell 2000 ETF', exchange: 'ARCA', status: 'active', tradable: true },
      { symbol: 'GLD', name: 'SPDR Gold Shares', exchange: 'ARCA', status: 'active', tradable: true },
      { symbol: 'TLT', name: 'iShares 20+ Year Treasury Bond ETF', exchange: 'NASDAQ', status: 'active', tradable: true },
      { symbol: 'BRK.A', name: 'Berkshire Hathaway Inc.', exchange: 'NYSE', status: 'active', tradable: true },
      { symbol: 'BRK.B', name: 'Berkshire Hathaway Inc.', exchange: 'NYSE', status: 'active', tradable: true },
      { symbol: 'JPM', name: 'JPMorgan Chase & Co.', exchange: 'NYSE', status: 'active', tradable: true },
      { symbol: 'BAC', name: 'Bank of America Corporation', exchange: 'NYSE', status: 'active', tradable: true },
      { symbol: 'KO', name: 'The Coca-Cola Company', exchange: 'NYSE', status: 'active', tradable: true },
      { symbol: 'PEP', name: 'PepsiCo Inc.', exchange: 'NASDAQ', status: 'active', tradable: true },
      { symbol: 'JNJ', name: 'Johnson & Johnson', exchange: 'NYSE', status: 'active', tradable: true },
      { symbol: 'PFE', name: 'Pfizer Inc.', exchange: 'NYSE', status: 'active', tradable: true },
      { symbol: 'WMT', name: 'Walmart Inc.', exchange: 'NYSE', status: 'active', tradable: true },
      { symbol: 'HD', name: 'The Home Depot Inc.', exchange: 'NYSE', status: 'active', tradable: true },
      { symbol: 'V', name: 'Visa Inc.', exchange: 'NYSE', status: 'active', tradable: true },
      { symbol: 'MA', name: 'Mastercard Incorporated', exchange: 'NYSE', status: 'active', tradable: true },
      // Add more popular symbols for better coverage
      { symbol: 'DIS', name: 'The Walt Disney Company', exchange: 'NYSE', status: 'active', tradable: true },
      { symbol: 'INTC', name: 'Intel Corporation', exchange: 'NASDAQ', status: 'active', tradable: true },
      { symbol: 'IBM', name: 'International Business Machines Corporation', exchange: 'NYSE', status: 'active', tradable: true },
      { symbol: 'GE', name: 'General Electric Company', exchange: 'NYSE', status: 'active', tradable: true },
      { symbol: 'F', name: 'Ford Motor Company', exchange: 'NYSE', status: 'active', tradable: true },
      { symbol: 'GM', name: 'General Motors Company', exchange: 'NYSE', status: 'active', tradable: true },
      { symbol: 'XOM', name: 'Exxon Mobil Corporation', exchange: 'NYSE', status: 'active', tradable: true },
      { symbol: 'CVX', name: 'Chevron Corporation', exchange: 'NYSE', status: 'active', tradable: true },
    ];
    
    try {
      // First, check if query matches any common symbols (for fast response)
      const knownMatches = commonSymbols.filter((asset: any) => 
        asset.symbol.includes(queryUpper) || 
        (asset.name && asset.name.toLowerCase().includes(queryLower))
      );
      
      // Always include exact symbol match for any user input (this allows trading any symbol)
      const exactSymbolMatch = {
        symbol: queryUpper,
        name: queryUpper,
        exchange: 'AUTO_DETECT',
        status: 'active',
        tradable: true
      };
      
      // If the query looks like a valid ticker symbol, prioritize it
      const isValidTicker = queryUpper.length >= 1 && queryUpper.length <= 8 && /^[A-Z0-9.^-]+$/.test(queryUpper);
      
      let results = [];
      
      if (isValidTicker) {
        // For valid ticker patterns, put the exact match first, then known matches
        results = [exactSymbolMatch, ...knownMatches.filter(m => m.symbol !== queryUpper)];
      } else {
        // For name searches, put known matches first, then exact match if it's short enough
        results = [...knownMatches];
        if (queryUpper.length <= 6) {
          results.push(exactSymbolMatch);
        }
      }
      
      // Sort results intelligently
      return results
        .sort((a: any, b: any) => {
          // Exact symbol matches get highest priority
          if (a.symbol === queryUpper && b.symbol !== queryUpper) return -1;
          if (b.symbol === queryUpper && a.symbol !== queryUpper) return 1;
          
          // Known symbols (with real company names) get priority over auto-generated ones
          const aIsKnown = a.name !== a.symbol;
          const bIsKnown = b.name !== b.symbol;
          if (aIsKnown && !bIsKnown) return -1;
          if (!aIsKnown && bIsKnown) return 1;
          
          // Starts-with matches for symbols
          const aStartsWithSymbol = a.symbol.startsWith(queryUpper);
          const bStartsWithSymbol = b.symbol.startsWith(queryUpper);
          if (aStartsWithSymbol && !bStartsWithSymbol) return -1;
          if (!aStartsWithSymbol && bStartsWithSymbol) return 1;
          
          // Shorter symbols generally better
          return a.symbol.length - b.symbol.length;
        })
        .slice(0, 10); // Limit to 10 results for UI performance
        
    } catch (error) {
      console.error('Error searching assets:', error);
      
      // Fallback: Always allow any symbol to be traded
      if (queryUpper.length >= 1 && queryUpper.length <= 8 && /^[A-Z0-9.^-]+$/.test(queryUpper)) {
        return [{ 
          symbol: queryUpper, 
          name: `${queryUpper} (Symbol)`, 
          exchange: 'AUTO_DETECT',
          status: 'active',
          tradable: true
        }];
      }
      
      return [];
    }
  },

  placeOrder: async (orderData: any): Promise<any> => {
    const res = await tradeClient.post('/v2/orders', orderData);
    return res.data;
  },

  /** Provide HTTP client for raw requests */
  getClient: () => tradeClient,
};